import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Pause, Play } from 'lucide-react';
import { cn } from '@/lib/utils';

// PICK THE PART OF THE SONG (owner 2026-10-21: "it actually chose the part of the song that i dont like ... give the
// user the option to choose which part of the song they want ... the same as they do it for facebook insta"): the
// song's waveform with a window as long as the version, dragged to the part you want; it plays that part as you
// pick (Instagram's music sticker). The loudest part is picked at first, as the server would. The start, in seconds,
// is what the cover is made from (canvas.js: the song field `songstart`).

const BARS = 120;

interface Decoded {
  peaks: number[];
  duration: number;
  loudest: number;
}

/** The song's shape (one peak per bar, 0..1), its length and where its loudest `len` seconds start. */
async function decode(file: File, len: number): Promise<Decoded> {
  const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctx();
  try {
    const audio = await ctx.decodeAudioData(await file.arrayBuffer());
    const data = audio.getChannelData(0);
    const per = Math.max(1, Math.floor(data.length / BARS));
    const peaks: number[] = [];
    for (let b = 0; b < BARS; b++) {
      let m = 0;
      for (let i = b * per, end = Math.min(data.length, (b + 1) * per); i < end; i += 16) m = Math.max(m, Math.abs(data[i]));
      peaks.push(m);
    }
    const top = Math.max(0.0001, ...peaks);
    // Energy per second, then the loudest stretch of `len` seconds.
    const secs = Math.floor(audio.duration);
    const rate = audio.sampleRate;
    const energy: number[] = [];
    for (let s = 0; s < secs; s++) {
      let e = 0;
      for (let i = s * rate, end = Math.min(data.length, (s + 1) * rate); i < end; i += 32) e += data[i] * data[i];
      energy.push(e);
    }
    let best = 0;
    let bestAt = 0;
    let run = 0;
    for (let s = 0; s < energy.length; s++) {
      run += energy[s] - (s >= len ? energy[s - len] : 0);
      if (s >= len - 1 && run > best) { best = run; bestAt = s - len + 1; }
    }
    return { peaks: peaks.map((p) => p / top), duration: audio.duration, loudest: bestAt };
  } finally {
    void ctx.close();
  }
}

const clock = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export function SongPartPicker({ file, len = 30, start, onStart, label, hint, playLabel, pauseLabel, onTime, onSong }: {
  file: File;
  len?: number;
  /** The chosen start in seconds, as a string ('' until chosen). */
  start: string;
  onStart: (s: string) => void;
  label: string;
  hint: string;
  playLabel: string;
  pauseLabel: string;
  /** While it plays: seconds into the chosen part (the live preview's lyrics follow it); null when stopped. */
  onTime?: (t: number | null) => void;
  /** The song's length once read. */
  onSong?: (seconds: number) => void;
}) {
  const [song, setSong] = useState<Decoded | null>(null);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  const audio = useRef<HTMLAudioElement>(null);
  const strip = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; at: number } | null>(null);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  useEffect(() => {
    let live = true;
    setSong(null);
    setFailed(false);
    decode(file, len).then((d) => {
      if (!live) return;
      setSong(d);
      onSong?.(d.duration);
      if (start === '') onStart(String(d.loudest));
    }).catch(() => live && setFailed(true));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, len]);

  const at = song ? Math.max(0, Math.min(song.duration - len, Number(start) || 0)) : 0;
  // Plays the chosen part, over and over, while open.
  useEffect(() => {
    const a = audio.current;
    if (!a) return undefined;
    const tick = () => {
      if (a.currentTime >= at + len || a.currentTime < at - 0.5) a.currentTime = at;
      onTime?.(Math.max(0, a.currentTime - at));
    };
    a.addEventListener('timeupdate', tick);
    return () => a.removeEventListener('timeupdate', tick);
  }, [at, len, onTime]);
  useEffect(() => { if (!playing) onTime?.(null); }, [playing, onTime]);

  if (failed) return null;
  if (song && song.duration <= len + 0.5) return null;
  const width = song ? Math.min(1, len / song.duration) : 0;
  const left = song ? at / song.duration : 0;

  const moveTo = (clientX: number, from: { x: number; at: number }) => {
    const box = strip.current?.getBoundingClientRect();
    if (!box || !song) return;
    const next = from.at + ((clientX - from.x) / box.width) * song.duration;
    onStart(String(Math.round(Math.max(0, Math.min(song.duration - len, next)))));
  };
  const down = (e: ReactPointerEvent<HTMLDivElement>) => {
    const box = strip.current?.getBoundingClientRect();
    if (!box || !song) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const x = (e.clientX - box.left) / box.width;
    // A tap outside the window centres it there; inside, it's grabbed where it is.
    const grabbed = x >= left && x <= left + width ? at : Math.max(0, Math.min(song.duration - len, x * song.duration - len / 2));
    drag.current = { x: e.clientX, at: grabbed };
    if (grabbed !== at) onStart(String(Math.round(grabbed)));
  };
  const up = () => {
    drag.current = null;
    const a = audio.current;
    if (a && playing) { a.currentTime = at; void a.play(); }
  };
  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); return; }
    a.currentTime = at;
    void a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  return (
    <div className="mt-4 rounded-2xl bg-white/[0.04] p-3" data-testid="qt-song-part">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[13.5px] font-semibold text-foreground">{label}</span>
        {song && <span className="text-[12.5px] font-bold tabular-nums text-primary">{clock(at)} – {clock(at + len)}</span>}
      </div>
      <div className="flex items-center gap-2.5">
        <button type="button" onClick={toggle} disabled={!song} aria-label={playing ? pauseLabel : playLabel} data-testid="qt-song-play"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-40">
          {playing ? <Pause className="h-5 w-5" aria-hidden /> : <Play className="ml-0.5 h-5 w-5" aria-hidden />}
        </button>
        <div
          ref={strip}
          className="relative h-14 min-w-0 flex-1 cursor-grab touch-none select-none active:cursor-grabbing"
          onPointerDown={down}
          onPointerMove={(e) => { if (drag.current) moveTo(e.clientX, drag.current); }}
          onPointerUp={up}
          onPointerCancel={up}
          role="slider"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={song ? Math.round(song.duration - len) : 0}
          aria-valuenow={Math.round(at)}
          aria-valuetext={`${clock(at)} – ${clock(at + len)}`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (!song || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
            e.preventDefault();
            onStart(String(Math.max(0, Math.min(Math.round(song.duration - len), Math.round(at) + (e.key === 'ArrowRight' ? 1 : -1)))));
          }}
        >
          <div className="absolute inset-0 flex items-center gap-[2px]">
            {(song?.peaks || Array.from({ length: BARS }, () => 0.15)).map((p, i) => {
              const inside = song && i / BARS >= left && i / BARS < left + width;
              return <span key={i} className={cn('min-w-0 flex-1 rounded-full', inside ? 'bg-primary' : 'bg-white/25', !song && 'animate-pulse')} style={{ height: `${Math.max(8, p * 100)}%` }} />;
            })}
          </div>
          {song && (
            <div aria-hidden className="pointer-events-none absolute inset-y-0 rounded-lg ring-2 ring-primary" style={{ left: `${left * 100}%`, width: `${width * 100}%`, background: 'hsl(var(--primary) / 0.12)' }} />
          )}
        </div>
      </div>
      <p className="mt-2 text-[12px] leading-snug text-muted-foreground">{hint}</p>
      <audio ref={audio} src={url} preload="auto" onPause={() => setPlaying(false)} />
    </div>
  );
}
